import * as XLSX from 'xlsx';

export interface ExcelExportOptions {
  fileName: string;
  sheetName?: string;
  data: any[];
  columns?: {
    key: string;
    label: string;
  }[];
  title?: string;
  subtitle?: string;
  logoUrl?: string;
}

export interface ExportFilterOptions {
  startDate?: string;
  endDate?: string;
  locationId?: string;
  facilityTypeId?: string;
  cleanlinessStatus?: 'all' | 'clean' | 'unclean';
  minScore?: number;
  maxScore?: number;
}

/**
 * Apply cell styling to worksheet
 */
const applyHeaderStyle = (worksheet: XLSX.WorkSheet, headerRow: number, numColumns: number) => {
  const headerStyle = {
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
    fill: { fgColor: { rgb: '2563EB' } }, // Blue background
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    },
  };

  for (let col = 0; col < numColumns; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: headerRow, c: col });
    if (worksheet[cellAddress]) {
      worksheet[cellAddress].s = headerStyle;
    }
  }
};

/**
 * Apply title styling
 */
const applyTitleStyle = (worksheet: XLSX.WorkSheet, titleRow: number) => {
  const titleCell = XLSX.utils.encode_cell({ r: titleRow, c: 0 });
  if (worksheet[titleCell]) {
    worksheet[titleCell].s = {
      font: { bold: true, sz: 18, color: { rgb: '1F2937' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }
};

/**
 * Apply subtitle styling
 */
const applySubtitleStyle = (worksheet: XLSX.WorkSheet, subtitleRow: number) => {
  const subtitleCell = XLSX.utils.encode_cell({ r: subtitleRow, c: 0 });
  if (worksheet[subtitleCell]) {
    worksheet[subtitleCell].s = {
      font: { sz: 12, color: { rgb: '6B7280' }, italic: true },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }
};

/**
 * Export data to Excel file with styling
 * @param options Export options including data, filename, and optional styling
 */
export const exportToExcel = ({
  fileName,
  sheetName = 'Sheet1',
  data,
  columns,
  title,
  subtitle,
}: ExcelExportOptions): void => {
  try {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    // Map data to use column labels as headers if columns are provided
    let exportData = data;

    if (columns && columns.length > 0) {
      exportData = data.map(row => {
        const mappedRow: Record<string, any> = {};
        columns.forEach(col => {
          mappedRow[col.label] = row[col.key] ?? '-';
        });
        return mappedRow;
      });
    }

    const headerKeys = Object.keys(exportData[0]);
    let currentRow = 0;
    const workbook = XLSX.utils.book_new();

    // Create array to hold all rows including title, subtitle, and data
    const allRows: any[] = [];

    // Add logo placeholder (text-based since actual images require more complex setup)
    if (title || subtitle) {
      allRows.push([]);
      currentRow++;
    }

    // Add title
    if (title) {
      allRows.push([title]);
      currentRow++;
      allRows.push([]); // Empty row for spacing
      currentRow++;
    }

    // Add subtitle
    if (subtitle) {
      allRows.push([subtitle]);
      currentRow++;
      allRows.push([]); // Empty row for spacing
      currentRow++;
    }

    // Add generated date/time
    const timestamp = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    allRows.push([`Generated: ${timestamp}`]);
    currentRow++;
    allRows.push([]); // Empty row for spacing
    currentRow++;

    // Add header row
    const headerRow = currentRow;
    allRows.push(headerKeys);
    currentRow++;

    // Add data rows
    exportData.forEach((row: any) => {
      allRows.push(headerKeys.map(key => row[key]));
    });

    // Create worksheet from array
    const worksheet = XLSX.utils.aoa_to_sheet(allRows);

    // Merge cells for title
    if (title) {
      const titleRow = 1;
      worksheet['!merges'] = worksheet['!merges'] || [];
      worksheet['!merges'].push({
        s: { r: titleRow, c: 0 },
        e: { r: titleRow, c: headerKeys.length - 1 },
      });
      applyTitleStyle(worksheet, titleRow);
    }

    // Merge cells for subtitle
    if (subtitle) {
      const subtitleRow = title ? 3 : 1;
      worksheet['!merges'] = worksheet['!merges'] || [];
      worksheet['!merges'].push({
        s: { r: subtitleRow, c: 0 },
        e: { r: subtitleRow, c: headerKeys.length - 1 },
      });
      applySubtitleStyle(worksheet, subtitleRow);
    }

    // Merge cells for timestamp
    const timestampRow = title && subtitle ? 5 : title ? 3 : subtitle ? 3 : 0;
    if (timestampRow > 0) {
      worksheet['!merges'] = worksheet['!merges'] || [];
      worksheet['!merges'].push({
        s: { r: timestampRow, c: 0 },
        e: { r: timestampRow, c: headerKeys.length - 1 },
      });
      const timestampCell = XLSX.utils.encode_cell({ r: timestampRow, c: 0 });
      if (worksheet[timestampCell]) {
        worksheet[timestampCell].s = {
          font: { sz: 10, color: { rgb: '9CA3AF' } },
          alignment: { horizontal: 'center' },
        };
      }
    }

    // Apply header styling
    applyHeaderStyle(worksheet, headerRow, headerKeys.length);

    // Style data rows with alternating colors
    const startDataRow = headerRow + 1;
    const endDataRow = startDataRow + data.length;
    for (let row = startDataRow; row < endDataRow; row++) {
      const isEvenRow = (row - startDataRow) % 2 === 0;
      for (let col = 0; col < headerKeys.length; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = {
            fill: isEvenRow ? { fgColor: { rgb: 'F9FAFB' } } : { fgColor: { rgb: 'FFFFFF' } },
            border: {
              top: { style: 'thin', color: { rgb: 'E5E7EB' } },
              bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
              left: { style: 'thin', color: { rgb: 'E5E7EB' } },
              right: { style: 'thin', color: { rgb: 'E5E7EB' } },
            },
            alignment: { vertical: 'center' },
          };
        }
      }
    }

    // Auto-size columns
    const maxWidth = 50;
    const minWidth = 12;
    const columnWidths = headerKeys.map((key, index) => {
      const maxLength = Math.max(key.length, ...exportData.map((row: any) => String(row[key] || '').length));
      return { wch: Math.min(Math.max(maxLength + 2, minWidth), maxWidth) };
    });
    worksheet['!cols'] = columnWidths;

    // Set row heights
    worksheet['!rows'] = [];
    if (title) worksheet['!rows'][1] = { hpt: 25 }; // Title row height
    worksheet['!rows'][headerRow] = { hpt: 22 }; // Header row height

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate filename with timestamp
    const fileTimestamp = new Date().toISOString().split('T')[0];
    const finalFileName = fileName.includes('.xlsx') ? fileName : `${fileName}_${fileTimestamp}.xlsx`;

    // Write file
    XLSX.writeFile(workbook, finalFileName);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export data to Excel');
  }
};

/**
 * Export multiple sheets to a single Excel file with styling
 */
export const exportMultipleSheetsToExcel = (
  fileName: string,
  sheets: Array<{
    sheetName: string;
    data: any[];
    columns?: { key: string; label: string }[];
    title?: string;
    subtitle?: string;
  }>
): void => {
  try {
    const workbook = XLSX.utils.book_new();

    sheets.forEach(({ sheetName, data, columns, title, subtitle }) => {
      // Use the single sheet export logic but just add to the same workbook
      let exportData = data;

      if (columns && columns.length > 0) {
        exportData = data.map(row => {
          const mappedRow: Record<string, any> = {};
          columns.forEach(col => {
            mappedRow[col.label] = row[col.key] ?? '-';
          });
          return mappedRow;
        });
      }

      const headerKeys = Object.keys(exportData[0] || {});
      const allRows: any[] = [];
      let currentRow = 0;

      // Add spacing
      if (title || subtitle) {
        allRows.push([]);
        currentRow++;
      }

      // Add title
      if (title) {
        allRows.push([title]);
        currentRow++;
        allRows.push([]);
        currentRow++;
      }

      // Add subtitle
      if (subtitle) {
        allRows.push([subtitle]);
        currentRow++;
        allRows.push([]);
        currentRow++;
      }

      // Add header and data
      allRows.push(headerKeys);
      exportData.forEach((row: any) => {
        allRows.push(headerKeys.map(key => row[key]));
      });

      const worksheet = XLSX.utils.aoa_to_sheet(allRows);

      // Auto-size columns
      if (exportData.length > 0) {
        const maxWidth = 50;
        const minWidth = 12;
        const columnWidths = headerKeys.map(key => {
          const maxLength = Math.max(key.length, ...exportData.map((row: any) => String(row[key] || '').length));
          return { wch: Math.min(Math.max(maxLength + 2, minWidth), maxWidth) };
        });
        worksheet['!cols'] = columnWidths;
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const timestamp = new Date().toISOString().split('T')[0];
    const finalFileName = fileName.includes('.xlsx') ? fileName : `${fileName}_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, finalFileName);
  } catch (error) {
    console.error('Error exporting multiple sheets to Excel:', error);
    throw new Error('Failed to export data to Excel');
  }
};
